import type {
  PickemBracketFormat,
  PickemBracketLayout,
  PickemBracketMatch,
  PickemCompetitor,
} from '@api/generated/types.gen';
import { Check, Trophy, X } from 'lucide-react';
import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { startingMatches } from '@fq/pickem-ui';
import { cn } from '@/lib/utils';

/**
 * On mobile (narrow viewports), shrinking the bracket far enough to fit makes
 * text unreadable — so we cap how small it gets and expose horizontal scroll
 * instead. On larger viewports we always fit the whole bracket on screen.
 */
const MOBILE_MIN_FIT_SCALE = 0.7;
const DESKTOP_BREAKPOINT_PX = 640;

function getMinFitScale(): number {
  if (typeof window === 'undefined') return 0;
  return window.innerWidth < DESKTOP_BREAKPOINT_PX ? MOBILE_MIN_FIT_SCALE : 0;
}

/**
 * Scales `children` down to fit the parent's available width. On desktop the
 * scale floor is 0 (always fits, no scroll). On mobile, the floor is
 * `MOBILE_MIN_FIT_SCALE` so the bracket stays readable and scrolls instead.
 */
function FitToWidth({
  width,
  height,
  children,
}: {
  width: number;
  height: number;
  children: ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const cw = el.clientWidth;
      if (cw <= 0 || width <= 0) return;
      const fitted = cw / width;
      setScale(Math.min(1, Math.max(fitted, getMinFitScale())));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [width]);

  const scaledWidth = width * scale;
  const scaledHeight = height * scale;

  return (
    <div
      ref={containerRef}
      className="overflow-x-auto"
      style={{ width: '100%', height: scaledHeight }}
    >
      <div
        style={{
          width: scaledWidth,
          height: scaledHeight,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            width,
            height,
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
import {
  computeEffectiveCompetitors,
  type EffectiveSlots,
} from '../lib/pickem-simulation';

export interface PickemBracketPickContext {
  topId?: string;
  bottomId?: string;
}

export interface PickemBracketProps {
  /** Number starting picks to match the player picker, independently of seeds. */
  showLineupNumbers?: boolean;
  matches: PickemBracketMatch[];
  competitors: PickemCompetitor[];
  userPicks?: Record<string, string>;
  /**
   * Predicted loser score per match — only meaningful for the finals row.
   * When present, the finals card shows a small "5–X" badge so the user
   * can see their prediction at a glance without re-opening the modal.
   */
  userPredictedLoserScores?: Record<string, number | null>;
  /**
   * Predicted bracket reset per match — only meaningful for the Grand Final
   * row. Shown alongside the score on the finals badge.
   */
  userPredictedResets?: Record<string, boolean | null>;
  /**
   * Points earned per match. Drives the "+X" badge above correct-pick
   * cards. Computed at the page level (the finals matchup gate needs
   * access to all matches + the user's semi picks).
   */
  userPointsByMatchId?: Record<string, number>;
  /**
   * Per-event visual layout. `to_the_center` is only honored for the main
   * single-elim section; auxiliary sections (upper/lower/grand_final) and
   * brackets that don't split cleanly fall back to left-to-right.
   */
  bracketLayout?: PickemBracketLayout;
  /** Drives loser-drop routing in the simulation (seeded top-8 differs). */
  bracketFormat?: PickemBracketFormat;
  onPickMatch?: (
    match: PickemBracketMatch,
    ctx: PickemBracketPickContext,
  ) => void;
  canPickMatch?: (
    match: PickemBracketMatch,
    ctx: PickemBracketPickContext,
  ) => boolean;
}

const SECTION_LABELS: Record<string, string> = {
  main: 'Bracket',
  upper: 'Upper Bracket',
  lower: 'Lower Bracket',
};

const LineupNumbersContext = createContext<ReadonlyMap<string, number>>(
  new Map(),
);

const MATCH_HEIGHT = 96;
const MATCH_WIDTH = 180;
// The final match is wider than the others to give it visual weight as the
// climax of the bracket. Centered layout reserves extra space in the middle;
// L→R mode extends the right edge.
const MATCH_WIDTH_FINAL = MATCH_WIDTH + 40;
const BASE_GAP = 14;
const UNIT = MATCH_HEIGHT + BASE_GAP;
const ROUND_SPACING = 24;
// Smaller spacing on either side of the centered final.
const FINAL_SIDE_SPACING = 20;
// Raise the centered final above the midpoint between its two semis,
// proportional to MATCH_HEIGHT. Gives the final a "podium" feel.
const FINAL_RAISE_RATIO = 1.0;
const HEADER_HEIGHT = 44;
// Extra vertical breathing room inside the FitToWidth viewport so the
// bottom row's card border, the finals-prediction badge that overhangs
// above the final card, and the inset rings don't get clipped by the
// scaled wrapper. The FitToWidth scales children to fit exactly; any
// per-card overflow (borders, ring outsets, position:-top badges) needs
// to be reserved here.
const VERTICAL_BUFFER = 24;
// Sub-pixel rounding in CSS transform:scale() can shave 1–2px off the
// scaled width, which clips the rightmost match card's border. A small
// horizontal buffer guarantees the bracket always fits with margin.
const HORIZONTAL_BUFFER = 12;

function getRoundLabel(
  roundNumber: number,
  totalRounds: number,
  section: string,
  isGrandFinal?: boolean,
): string {
  if (isGrandFinal) return 'Grand Final';
  if (roundNumber === totalRounds)
    return section === 'upper'
      ? 'Upper Finals'
      : section === 'lower'
        ? 'Lower Finals'
        : 'Final';
  if (roundNumber === totalRounds - 1) return 'Semifinals';
  if (roundNumber === totalRounds - 2) return 'Quarterfinals';
  if (section === 'lower') return `Lower R${roundNumber}`;
  return `Round ${roundNumber}`;
}

export function PickemBracket({
  showLineupNumbers = false,
  matches,
  competitors,
  userPicks,
  userPredictedLoserScores,
  userPredictedResets,
  userPointsByMatchId,
  bracketLayout = 'left_to_right',
  bracketFormat,
  onPickMatch,
  canPickMatch,
}: PickemBracketProps) {
  const competitorMap = new Map(competitors.map((c) => [c.id, c]));
  const effectiveSlots = computeEffectiveCompetitors(
    matches,
    userPicks ?? {},
    bracketFormat,
  );

  const sections = new Map<string, PickemBracketMatch[]>();
  for (const match of matches) {
    const section = match.section ?? 'main';
    const existing = sections.get(section) || [];
    existing.push(match);
    sections.set(section, existing);
  }

  const upperKey = sections.has('upper') ? 'upper' : 'main';
  const upperMatches = sections.get(upperKey);
  const grandFinalMatches = sections.get('grand_final');

  if (upperMatches && grandFinalMatches && grandFinalMatches.length > 0) {
    const maxUpperRound = Math.max(...upperMatches.map((m) => m.roundNumber));
    for (const gfMatch of grandFinalMatches) {
      upperMatches.push({
        ...gfMatch,
        roundNumber: maxUpperRound + 1,
      });
    }
    sections.delete('grand_final');
  }

  const sectionOrder = ['main', 'upper', 'lower'];
  const sortedSections = [...sections.entries()].sort(
    ([a], [b]) =>
      (sectionOrder.indexOf(a) === -1 ? 99 : sectionOrder.indexOf(a)) -
      (sectionOrder.indexOf(b) === -1 ? 99 : sectionOrder.indexOf(b)),
  );

  const lineupNumbers = new Map(
    showLineupNumbers
      ? startingMatches(matches, bracketFormat).map((match, index) => [
          match.id,
          index * 2 + 1,
        ] as const)
      : [],
  );

  return (
    <LineupNumbersContext.Provider value={lineupNumbers}>
    <div className="space-y-8">
      {sortedSections.map(([sectionKey, sectionMatches]) => (
        <BracketSection
          key={sectionKey}
          sectionKey={sectionKey}
          matches={sectionMatches}
          competitorMap={competitorMap}
          effectiveSlots={effectiveSlots}
          userPicks={userPicks}
          userPredictedLoserScores={userPredictedLoserScores}
          userPredictedResets={userPredictedResets}
          userPointsByMatchId={userPointsByMatchId}
          bracketLayout={bracketLayout}
          onPickMatch={onPickMatch}
          canPickMatch={canPickMatch}
        />
      ))}
    </div>
    </LineupNumbersContext.Provider>
  );
}

type RoundData = {
  roundNumber: number;
  matches: PickemBracketMatch[];
  isGrandFinal: boolean;
};

/**
 * Position rules for the standard left-to-right algorithm. First round spaces
 * matches evenly; each subsequent round either centers on its two feeders
 * (merge round) or aligns with its single feeder.
 */
function computeYPositions(
  roundData: ReadonlyArray<RoundData>,
): Map<string, number> {
  const yPositions = new Map<string, number>();
  for (let ri = 0; ri < roundData.length; ri++) {
    const { matches: rMatches } = roundData[ri];
    if (ri === 0) {
      for (let i = 0; i < rMatches.length; i++) {
        yPositions.set(rMatches[i].id, i * UNIT);
      }
      continue;
    }
    const prevMatches = roundData[ri - 1].matches;
    const isMergeRound = rMatches.length < prevMatches.length;
    for (let i = 0; i < rMatches.length; i++) {
      if (isMergeRound) {
        const f1 = prevMatches[i * 2];
        const f2 = prevMatches[i * 2 + 1];
        if (f1 && f2) {
          const y1 = yPositions.get(f1.id) ?? 0;
          const y2 = yPositions.get(f2.id) ?? 0;
          yPositions.set(rMatches[i].id, (y1 + y2) / 2);
        } else if (f1) {
          yPositions.set(rMatches[i].id, yPositions.get(f1.id) ?? 0);
        } else {
          yPositions.set(rMatches[i].id, i * UNIT * Math.pow(2, ri));
        }
      } else {
        const feeder = prevMatches[i];
        if (feeder) {
          yPositions.set(rMatches[i].id, yPositions.get(feeder.id) ?? 0);
        } else {
          yPositions.set(rMatches[i].id, i * UNIT);
        }
      }
    }
  }
  return yPositions;
}

/**
 * The centered layout requires every non-final round to have an even number
 * of matches (so we can split into halves) and the final round to have
 * exactly one match.
 */
function canApplyCenteredLayout(
  roundData: ReadonlyArray<RoundData>,
): boolean {
  if (roundData.length === 0) return false;
  const last = roundData[roundData.length - 1];
  if (last.matches.length !== 1) return false;
  for (let i = 0; i < roundData.length - 1; i++) {
    if (roundData[i].matches.length % 2 !== 0) return false;
  }
  return true;
}

function BracketSection({
  sectionKey,
  matches,
  competitorMap,
  effectiveSlots,
  userPicks,
  userPredictedLoserScores,
  userPredictedResets,
  userPointsByMatchId,
  bracketLayout = 'left_to_right',
  onPickMatch,
  canPickMatch,
}: {
  sectionKey: string;
  matches: PickemBracketMatch[];
  competitorMap: Map<string, PickemCompetitor>;
  effectiveSlots: Map<string, EffectiveSlots>;
  userPicks?: Record<string, string>;
  userPredictedLoserScores?: Record<string, number | null>;
  userPredictedResets?: Record<string, boolean | null>;
  userPointsByMatchId?: Record<string, number>;
  bracketLayout?: PickemBracketLayout;
  onPickMatch?: (
    match: PickemBracketMatch,
    ctx: PickemBracketPickContext,
  ) => void;
  canPickMatch?: (
    match: PickemBracketMatch,
    ctx: PickemBracketPickContext,
  ) => boolean;
}) {
  const rounds = new Map<number, PickemBracketMatch[]>();
  for (const match of matches) {
    const existing = rounds.get(match.roundNumber) || [];
    existing.push(match);
    rounds.set(match.roundNumber, existing);
  }

  const sortedRounds = [...rounds.entries()].sort(([a], [b]) => a - b);
  const totalRounds = sortedRounds.length;
  if (totalRounds === 0) return null;

  const roundData: RoundData[] = sortedRounds.map(
    ([roundNumber, roundMatches]) => ({
      roundNumber,
      matches: [...roundMatches].sort((a, b) => a.matchIndex - b.matchIndex),
      isGrandFinal: roundMatches.some((m) => m.section === 'grand_final'),
    }),
  );

  // Centered layout only applies to the primary single-elim section (`main`)
  // and only when each round splits cleanly. Auxiliary sections (upper/lower
  // bracket of double-elim) always render left-to-right.
  if (
    bracketLayout === 'to_the_center' &&
    sectionKey === 'main' &&
    canApplyCenteredLayout(roundData)
  ) {
    return (
      <CenteredBracketSection
        sectionKey={sectionKey}
        roundData={roundData}
        competitorMap={competitorMap}
        effectiveSlots={effectiveSlots}
        userPicks={userPicks}
        userPredictedLoserScores={userPredictedLoserScores}
        userPredictedResets={userPredictedResets}
        userPointsByMatchId={userPointsByMatchId}
        onPickMatch={onPickMatch}
        canPickMatch={canPickMatch}
      />
    );
  }

  const yPositions = computeYPositions(roundData);

  const allYValues = [...yPositions.values()];
  const totalHeight =
    allYValues.length > 0 ? Math.max(...allYValues) + MATCH_HEIGHT : MATCH_HEIGHT;
  // L→R brackets end with a wider "final" box in main/upper sections, so
  // reserve the extra width at the right edge.
  const hasFinal = sectionKey !== 'lower';
  const totalWidth =
    totalRounds * MATCH_WIDTH +
    (totalRounds - 1) * ROUND_SPACING +
    (hasFinal ? MATCH_WIDTH_FINAL - MATCH_WIDTH : 0);
  const normalRoundCount = roundData.filter((r) => !r.isGrandFinal).length;

  const connectors: {
    key: string;
    f1Y: number;
    f2Y?: number;
    mY: number;
    fromX: number;
    toX: number;
  }[] = [];

  for (let ri = 1; ri < roundData.length; ri++) {
    const prevRound = roundData[ri - 1];
    const currentRound = roundData[ri];
    const isMergeRound = currentRound.matches.length < prevRound.matches.length;

    for (let mi = 0; mi < currentRound.matches.length; mi++) {
      const match = currentRound.matches[mi];
      const fromX = (ri - 1) * (MATCH_WIDTH + ROUND_SPACING) + MATCH_WIDTH;
      const toX = ri * (MATCH_WIDTH + ROUND_SPACING);
      const mY = (yPositions.get(match.id) ?? 0) + MATCH_HEIGHT / 2;

      if (isMergeRound) {
        const f1 = prevRound.matches[mi * 2];
        const f2 = prevRound.matches[mi * 2 + 1];
        if (!f1) continue;

        const f1Y = (yPositions.get(f1.id) ?? 0) + MATCH_HEIGHT / 2;
        const f2Y = f2
          ? (yPositions.get(f2.id) ?? 0) + MATCH_HEIGHT / 2
          : undefined;

        connectors.push({ key: match.id, f1Y, f2Y, mY, fromX, toX });
      } else {
        const feeder = prevRound.matches[mi];
        if (!feeder) continue;
        const fY = (yPositions.get(feeder.id) ?? 0) + MATCH_HEIGHT / 2;
        connectors.push({ key: match.id, f1Y: fY, mY, fromX, toX });
      }
    }
  }

  return (
    <div>
      {sectionKey !== 'main' && (
        <h3 className="text-base font-semibold mb-3">
          {SECTION_LABELS[sectionKey] || sectionKey}
        </h3>
      )}

      <FitToWidth width={totalWidth + HORIZONTAL_BUFFER} height={totalHeight + HEADER_HEIGHT + VERTICAL_BUFFER}>
       <div
        className="relative"
        style={{ width: totalWidth, height: totalHeight + HEADER_HEIGHT + VERTICAL_BUFFER }}
      >
        {roundData.map((round, ri) => {
          const isLastRound = ri === roundData.length - 1;
          const labelWidth =
            (isLastRound && hasFinal) || round.isGrandFinal
              ? MATCH_WIDTH_FINAL
              : MATCH_WIDTH;
          return (
          <div
            key={round.roundNumber}
            className="absolute text-lg font-semibold uppercase tracking-wide text-foreground whitespace-nowrap flex items-center justify-center gap-1.5"
            style={{
              left: ri * (MATCH_WIDTH + ROUND_SPACING),
              top: 0,
              width: labelWidth,
            }}
          >
            {round.isGrandFinal && (
              <Trophy className="h-4 w-4 text-yellow-500" />
            )}
            {getRoundLabel(
              round.roundNumber,
              normalRoundCount,
              sectionKey,
              round.isGrandFinal,
            )}
          </div>
          );
        })}

        <svg
          className="absolute pointer-events-none"
          style={{ top: HEADER_HEIGHT, left: 0 }}
          width={totalWidth}
          height={totalHeight}
        >
          {connectors.map((c) => {
            const midX = (c.fromX + c.toX) / 2;
            if (c.f2Y === undefined) {
              return (
                <g key={c.key}>
                  <line
                    x1={c.fromX}
                    y1={c.f1Y}
                    x2={midX}
                    y2={c.f1Y}
                    className="stroke-border"
                    strokeWidth="1"
                  />
                  <line
                    x1={midX}
                    y1={c.f1Y}
                    x2={midX}
                    y2={c.mY}
                    className="stroke-border"
                    strokeWidth="1"
                  />
                  <line
                    x1={midX}
                    y1={c.mY}
                    x2={c.toX}
                    y2={c.mY}
                    className="stroke-border"
                    strokeWidth="1"
                  />
                </g>
              );
            }
            return (
              <g key={c.key}>
                <line
                  x1={c.fromX}
                  y1={c.f1Y}
                  x2={midX}
                  y2={c.f1Y}
                  className="stroke-border"
                  strokeWidth="1"
                />
                <line
                  x1={c.fromX}
                  y1={c.f2Y}
                  x2={midX}
                  y2={c.f2Y}
                  className="stroke-border"
                  strokeWidth="1"
                />
                <line
                  x1={midX}
                  y1={c.f1Y}
                  x2={midX}
                  y2={c.f2Y}
                  className="stroke-border"
                  strokeWidth="1"
                />
                <line
                  x1={midX}
                  y1={c.mY}
                  x2={c.toX}
                  y2={c.mY}
                  className="stroke-border"
                  strokeWidth="1"
                />
              </g>
            );
          })}
        </svg>

        {roundData.map((round, ri) =>
          round.matches.map((match) => {
            const y = yPositions.get(match.id) ?? 0;
            const x = ri * (MATCH_WIDTH + ROUND_SPACING);
            const isFinal =
              round.isGrandFinal ||
              (ri === roundData.length - 1 && sectionKey !== 'lower');

            return (
              <div
                key={match.id}
                className="absolute"
                style={{ left: x, top: y + HEADER_HEIGHT }}
              >
                <MatchCard
                  match={match}
                  competitorMap={competitorMap}
                  effective={effectiveSlots.get(match.id) ?? {}}
                  userPickId={userPicks?.[match.id]}
                  predictedLoserScore={userPredictedLoserScores?.[match.id] ?? null}
                  predictedReset={userPredictedResets?.[match.id] ?? null}
                  pointsEarned={userPointsByMatchId?.[match.id]}
                  onPickMatch={onPickMatch}
                  canPickMatch={canPickMatch}
                  isFinal={isFinal}
                />
              </div>
            );
          }),
        )}
       </div>
      </FitToWidth>
    </div>
  );
}

/**
 * Single-elim "to the center" layout. The first half of each non-final round
 * fans out left-to-right on the left, the second half mirrors right-to-left
 * on the right, and the final sits in the middle bridging both semifinals.
 * Connectors flip direction on the right side; the final connector is split
 * into two horizontal halves (one from each semi).
 */
function CenteredBracketSection({
  sectionKey,
  roundData,
  competitorMap,
  effectiveSlots,
  userPicks,
  userPredictedLoserScores,
  userPredictedResets,
  userPointsByMatchId,
  onPickMatch,
  canPickMatch,
}: {
  sectionKey: string;
  roundData: RoundData[];
  competitorMap: Map<string, PickemCompetitor>;
  effectiveSlots: Map<string, EffectiveSlots>;
  userPicks?: Record<string, string>;
  userPredictedLoserScores?: Record<string, number | null>;
  userPredictedResets?: Record<string, boolean | null>;
  userPointsByMatchId?: Record<string, number>;
  onPickMatch?: (
    match: PickemBracketMatch,
    ctx: PickemBracketPickContext,
  ) => void;
  canPickMatch?: (
    match: PickemBracketMatch,
    ctx: PickemBracketPickContext,
  ) => boolean;
}) {
  const finalRoundIdx = roundData.length - 1;
  const finalMatch = roundData[finalRoundIdx]?.matches[0];

  // Split each non-final round in half; reverse the right half so its first
  // entry is the topmost match (mirrors the left side visually).
  const leftRoundData: RoundData[] = roundData.slice(0, finalRoundIdx).map(
    (r) => ({
      ...r,
      matches: r.matches.slice(0, r.matches.length / 2),
    }),
  );
  const rightRoundData: RoundData[] = roundData.slice(0, finalRoundIdx).map(
    (r) => ({
      ...r,
      matches: r.matches.slice(r.matches.length / 2),
    }),
  );

  const yLeft = computeYPositions(leftRoundData);
  const yRight = computeYPositions(rightRoundData);

  const sideRoundCount = leftRoundData.length;
  const sideBlockWidth =
    sideRoundCount * MATCH_WIDTH +
    Math.max(0, sideRoundCount - 1) * ROUND_SPACING;
  const totalWidth =
    2 * sideBlockWidth + 2 * FINAL_SIDE_SPACING + MATCH_WIDTH_FINAL;

  const leftX = (ri: number) => ri * (MATCH_WIDTH + ROUND_SPACING);
  const rightX = (ri: number) =>
    totalWidth - (ri + 1) * MATCH_WIDTH - ri * ROUND_SPACING;
  const finalX = sideBlockWidth + FINAL_SIDE_SPACING;

  const lastLeftSemi =
    sideRoundCount > 0 ? leftRoundData[sideRoundCount - 1].matches[0] : null;
  const lastRightSemi =
    sideRoundCount > 0 ? rightRoundData[sideRoundCount - 1].matches[0] : null;

  let finalY = 0;
  if (lastLeftSemi && lastRightSemi) {
    const ly = yLeft.get(lastLeftSemi.id) ?? 0;
    const ry = yRight.get(lastRightSemi.id) ?? 0;
    finalY = Math.max(0, (ly + ry) / 2 - MATCH_HEIGHT * FINAL_RAISE_RATIO);
  } else if (lastLeftSemi) {
    finalY = yLeft.get(lastLeftSemi.id) ?? 0;
  } else if (lastRightSemi) {
    finalY = yRight.get(lastRightSemi.id) ?? 0;
  }

  const allYValues: number[] = [
    ...yLeft.values(),
    ...yRight.values(),
    finalY,
  ];
  const totalHeight =
    allYValues.length > 0 ? Math.max(...allYValues) + MATCH_HEIGHT : MATCH_HEIGHT;

  const connectors: {
    key: string;
    f1Y: number;
    f2Y?: number;
    mY: number;
    fromX: number;
    toX: number;
  }[] = [];

  // Left side connectors flow rightward (current L→R algorithm).
  for (let ri = 1; ri < leftRoundData.length; ri++) {
    const prev = leftRoundData[ri - 1];
    const cur = leftRoundData[ri];
    const isMerge = cur.matches.length < prev.matches.length;
    for (let mi = 0; mi < cur.matches.length; mi++) {
      const m = cur.matches[mi];
      const fromX = leftX(ri - 1) + MATCH_WIDTH;
      const toX = leftX(ri);
      const mY = (yLeft.get(m.id) ?? 0) + MATCH_HEIGHT / 2;
      if (isMerge) {
        const f1 = prev.matches[mi * 2];
        const f2 = prev.matches[mi * 2 + 1];
        if (!f1) continue;
        const f1Y = (yLeft.get(f1.id) ?? 0) + MATCH_HEIGHT / 2;
        const f2Y = f2
          ? (yLeft.get(f2.id) ?? 0) + MATCH_HEIGHT / 2
          : undefined;
        connectors.push({ key: `L-${m.id}`, f1Y, f2Y, mY, fromX, toX });
      } else {
        const feeder = prev.matches[mi];
        if (!feeder) continue;
        const fY = (yLeft.get(feeder.id) ?? 0) + MATCH_HEIGHT / 2;
        connectors.push({ key: `L-${m.id}`, f1Y: fY, mY, fromX, toX });
      }
    }
  }

  // Right side connectors flow leftward — fromX is the LEFT edge of the
  // feeder column, toX is the RIGHT edge of the current column. The shared
  // SVG renderer below handles fromX > toX without modification (the L-shape
  // just inverts horizontally).
  for (let ri = 1; ri < rightRoundData.length; ri++) {
    const prev = rightRoundData[ri - 1];
    const cur = rightRoundData[ri];
    const isMerge = cur.matches.length < prev.matches.length;
    for (let mi = 0; mi < cur.matches.length; mi++) {
      const m = cur.matches[mi];
      const fromX = rightX(ri - 1);
      const toX = rightX(ri) + MATCH_WIDTH;
      const mY = (yRight.get(m.id) ?? 0) + MATCH_HEIGHT / 2;
      if (isMerge) {
        const f1 = prev.matches[mi * 2];
        const f2 = prev.matches[mi * 2 + 1];
        if (!f1) continue;
        const f1Y = (yRight.get(f1.id) ?? 0) + MATCH_HEIGHT / 2;
        const f2Y = f2
          ? (yRight.get(f2.id) ?? 0) + MATCH_HEIGHT / 2
          : undefined;
        connectors.push({ key: `R-${m.id}`, f1Y, f2Y, mY, fromX, toX });
      } else {
        const feeder = prev.matches[mi];
        if (!feeder) continue;
        const fY = (yRight.get(feeder.id) ?? 0) + MATCH_HEIGHT / 2;
        connectors.push({ key: `R-${m.id}`, f1Y: fY, mY, fromX, toX });
      }
    }
  }

  // Final connector: drawn as two single-feeder L-shapes, one from each semi.
  if (finalMatch && lastLeftSemi) {
    const semiY = (yLeft.get(lastLeftSemi.id) ?? 0) + MATCH_HEIGHT / 2;
    connectors.push({
      key: `FINAL-L-${finalMatch.id}`,
      f1Y: semiY,
      mY: finalY + MATCH_HEIGHT / 2,
      fromX: leftX(sideRoundCount - 1) + MATCH_WIDTH,
      toX: finalX,
    });
  }
  if (finalMatch && lastRightSemi) {
    const semiY = (yRight.get(lastRightSemi.id) ?? 0) + MATCH_HEIGHT / 2;
    connectors.push({
      key: `FINAL-R-${finalMatch.id}`,
      f1Y: semiY,
      mY: finalY + MATCH_HEIGHT / 2,
      fromX: rightX(sideRoundCount - 1),
      toX: finalX + MATCH_WIDTH_FINAL,
    });
  }

  const normalRoundCount = roundData.length;

  return (
    <div>
      {sectionKey !== 'main' && (
        <h3 className="text-base font-semibold mb-3">
          {SECTION_LABELS[sectionKey] || sectionKey}
        </h3>
      )}

      <FitToWidth
        width={totalWidth + HORIZONTAL_BUFFER}
        height={totalHeight + HEADER_HEIGHT + VERTICAL_BUFFER}
      >
       <div
        className="relative pb-2"
        style={{ width: totalWidth, height: totalHeight + HEADER_HEIGHT + VERTICAL_BUFFER }}
      >
        {/* Round labels — left side, right side (mirrored), and centered final */}
        {leftRoundData.map((round, ri) => (
          <div
            key={`label-L-${round.roundNumber}`}
            className="absolute text-lg font-semibold uppercase tracking-wide text-foreground whitespace-nowrap flex items-center justify-center gap-1.5"
            style={{ left: leftX(ri), top: 0, width: MATCH_WIDTH }}
          >
            {getRoundLabel(round.roundNumber, normalRoundCount, sectionKey)}
          </div>
        ))}
        {rightRoundData.map((round, ri) => (
          <div
            key={`label-R-${round.roundNumber}`}
            className="absolute text-lg font-semibold uppercase tracking-wide text-foreground whitespace-nowrap flex items-center justify-center gap-1.5"
            style={{ left: rightX(ri), top: 0, width: MATCH_WIDTH }}
          >
            {getRoundLabel(round.roundNumber, normalRoundCount, sectionKey)}
          </div>
        ))}
        {finalMatch && (
          <div
            className="absolute text-lg font-semibold uppercase tracking-wide text-foreground whitespace-nowrap flex items-center justify-center gap-1.5"
            style={{ left: finalX, top: 0, width: MATCH_WIDTH_FINAL }}
          >
            {getRoundLabel(
              roundData[finalRoundIdx].roundNumber,
              normalRoundCount,
              sectionKey,
            )}
          </div>
        )}

        <svg
          className="absolute pointer-events-none"
          style={{ top: HEADER_HEIGHT, left: 0 }}
          width={totalWidth}
          height={totalHeight}
        >
          {connectors.map((c) => {
            const midX = (c.fromX + c.toX) / 2;
            if (c.f2Y === undefined) {
              return (
                <g key={c.key}>
                  <line
                    x1={c.fromX}
                    y1={c.f1Y}
                    x2={midX}
                    y2={c.f1Y}
                    className="stroke-border"
                    strokeWidth="1"
                  />
                  <line
                    x1={midX}
                    y1={c.f1Y}
                    x2={midX}
                    y2={c.mY}
                    className="stroke-border"
                    strokeWidth="1"
                  />
                  <line
                    x1={midX}
                    y1={c.mY}
                    x2={c.toX}
                    y2={c.mY}
                    className="stroke-border"
                    strokeWidth="1"
                  />
                </g>
              );
            }
            return (
              <g key={c.key}>
                <line
                  x1={c.fromX}
                  y1={c.f1Y}
                  x2={midX}
                  y2={c.f1Y}
                  className="stroke-border"
                  strokeWidth="1"
                />
                <line
                  x1={c.fromX}
                  y1={c.f2Y}
                  x2={midX}
                  y2={c.f2Y}
                  className="stroke-border"
                  strokeWidth="1"
                />
                <line
                  x1={midX}
                  y1={c.f1Y}
                  x2={midX}
                  y2={c.f2Y}
                  className="stroke-border"
                  strokeWidth="1"
                />
                <line
                  x1={midX}
                  y1={c.mY}
                  x2={c.toX}
                  y2={c.mY}
                  className="stroke-border"
                  strokeWidth="1"
                />
              </g>
            );
          })}
        </svg>

        {leftRoundData.map((round, ri) =>
          round.matches.map((match) => {
            const y = yLeft.get(match.id) ?? 0;
            return (
              <div
                key={match.id}
                className="absolute"
                style={{ left: leftX(ri), top: y + HEADER_HEIGHT }}
              >
                <MatchCard
                  match={match}
                  competitorMap={competitorMap}
                  effective={effectiveSlots.get(match.id) ?? {}}
                  userPickId={userPicks?.[match.id]}
                  pointsEarned={userPointsByMatchId?.[match.id]}
                  onPickMatch={onPickMatch}
                  canPickMatch={canPickMatch}
                  isFinal={false}
                  flowDirection="ltr"
                />
              </div>
            );
          }),
        )}
        {rightRoundData.map((round, ri) =>
          round.matches.map((match) => {
            const y = yRight.get(match.id) ?? 0;
            return (
              <div
                key={match.id}
                className="absolute"
                style={{ left: rightX(ri), top: y + HEADER_HEIGHT }}
              >
                <MatchCard
                  match={match}
                  competitorMap={competitorMap}
                  effective={effectiveSlots.get(match.id) ?? {}}
                  userPickId={userPicks?.[match.id]}
                  pointsEarned={userPointsByMatchId?.[match.id]}
                  onPickMatch={onPickMatch}
                  canPickMatch={canPickMatch}
                  isFinal={false}
                  flowDirection="rtl"
                />
              </div>
            );
          }),
        )}
        {finalMatch && (
          <div
            key={finalMatch.id}
            className="absolute"
            style={{ left: finalX, top: finalY + HEADER_HEIGHT }}
          >
            <MatchCard
              match={finalMatch}
              competitorMap={competitorMap}
              effective={effectiveSlots.get(finalMatch.id) ?? {}}
              userPickId={userPicks?.[finalMatch.id]}
              predictedLoserScore={
                userPredictedLoserScores?.[finalMatch.id] ?? null
              }
              predictedReset={userPredictedResets?.[finalMatch.id] ?? null}
              pointsEarned={userPointsByMatchId?.[finalMatch.id]}
              onPickMatch={onPickMatch}
              canPickMatch={canPickMatch}
              isFinal
            />
          </div>
        )}
       </div>
      </FitToWidth>
    </div>
  );
}

function MatchCard({
  match,
  competitorMap,
  effective,
  userPickId,
  predictedLoserScore,
  predictedReset,
  pointsEarned,
  onPickMatch,
  canPickMatch,
  isFinal,
  flowDirection,
}: {
  match: PickemBracketMatch;
  competitorMap: Map<string, PickemCompetitor>;
  effective: EffectiveSlots;
  userPickId: string | undefined;
  /** Only meaningful when isFinal — renders a "5–X" badge on the card. */
  predictedLoserScore?: number | null;
  /** Grand Final only — predicted bracket reset, shown on the finals badge. */
  predictedReset?: boolean | null;
  /** Total points the user earned for this match. Drives the "+X" badge
   * above correct picks. `undefined` or 0 → no badge. */
  pointsEarned?: number;
  onPickMatch?: (
    match: PickemBracketMatch,
    ctx: PickemBracketPickContext,
  ) => void;
  canPickMatch?: (
    match: PickemBracketMatch,
    ctx: PickemBracketPickContext,
  ) => boolean;
  isFinal: boolean;
  /** Centered layout's right-side matches advance right→left toward the
   * final, so their gold "correct pick" particles should drift in reverse.
   * Defaults to 'ltr'. */
  flowDirection?: 'ltr' | 'rtl';
}) {
  const firstPickNumber = useContext(LineupNumbersContext).get(match.id);
  const topId = match.competitorTopId ?? effective.topId;
  const bottomId = match.competitorBottomId ?? effective.bottomId;
  const topComp = topId ? competitorMap.get(topId) : null;
  const bottomComp = bottomId ? competitorMap.get(bottomId) : null;

  const topPredicted = !match.competitorTopId && !!effective.topId;
  const bottomPredicted = !match.competitorBottomId && !!effective.bottomId;

  const hasBothCompetitors = !!topComp && !!bottomComp;
  const ctx: PickemBracketPickContext = {
    topId: topId ?? undefined,
    bottomId: bottomId ?? undefined,
  };
  const canPick =
    !!onPickMatch &&
    hasBothCompetitors &&
    (!canPickMatch || canPickMatch(match, ctx));
  const isCompleted = match.status === 'completed';

  // "Orphaned" pick: the user saved a pick for this match, but neither of
  // the resolved competitors equals it — meaning an earlier-round prediction
  // failed and the picked competitor never made it to this match. Without
  // surfacing it, the box looks like it just has no pick / no styling and
  // it's easy to mistake for a bug. We only consider the case where BOTH
  // slots have real competitors assigned (else the pick might still match
  // once the TBD slot resolves).
  const orphanedPickName =
    userPickId && topId && bottomId && userPickId !== topId && userPickId !== bottomId
      ? competitorMap.get(userPickId)?.name ?? null
      : null;

  // Winner's target score on the finals badge: the Grand Final is first-to-3,
  // single-elim finals are first-to-5.
  const winTarget = match.section === 'grand_final' ? 3 : 5;

  return (
    <div
      style={{ width: isFinal ? MATCH_WIDTH_FINAL : MATCH_WIDTH }}
      className="relative"
    >
      {(match.scoreTop != null || match.scoreBottom != null) && <div className="text-xs text-center text-muted-foreground mb-1">Live score: {match.scoreTop ?? '–'} – {match.scoreBottom ?? '–'}</div>}
      {isFinal &&
        (predictedLoserScore != null ||
          match.actualLoserScore != null ||
          predictedReset != null ||
          match.actualReset != null) && (
        // Sit above the card. Rendered on the *outer wrapper* (not inside
        // MatchCard) so the card's overflow-hidden — which we need for the
        // rounded corners + bg fills — doesn't clip the overhang. Shows
        // predicted + actual when both are available so the delta is
        // visible at a glance. Includes the points-earned segment when the
        // user scored on this final (i.e. matchup gate passed).
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 whitespace-nowrap rounded-md bg-amber-500/15 px-2 py-0.5 ring-1 ring-amber-500/50 text-[11px] font-bold tabular-nums text-amber-300 shadow-sm">
          {(predictedLoserScore != null || predictedReset != null) && (
            <span>
              <span className="text-amber-200/60 font-normal mr-1">PICK</span>
              {predictedLoserScore != null && (
                <>
                  {winTarget}–{predictedLoserScore}
                </>
              )}
              {predictedReset != null && (
                <span className="ml-1 font-normal text-amber-200/80">
                  {predictedReset ? '+ reset' : '· no reset'}
                </span>
              )}
            </span>
          )}
          {(predictedLoserScore != null || predictedReset != null) &&
            (match.actualLoserScore != null || match.actualReset != null) && (
              <span className="text-amber-200/40">•</span>
            )}
          {(match.actualLoserScore != null || match.actualReset != null) && (
            <span className="text-emerald-300">
              <span className="text-emerald-200/60 font-normal mr-1">FINAL</span>
              {match.actualLoserScore != null && (
                <>
                  {winTarget}–{match.actualLoserScore}
                </>
              )}
              {match.actualReset != null && (
                <span className="ml-1 font-normal text-emerald-200/80">
                  {match.actualReset ? '+ reset' : '· no reset'}
                </span>
              )}
            </span>
          )}
          {pointsEarned != null && pointsEarned > 0 && (
            <>
              <span className="text-amber-200/40">•</span>
              <span className="text-emerald-300">+{pointsEarned}</span>
            </>
          )}
        </div>
      )}
      {/* Non-finals correct pick: a standalone "+X" badge above the card,
        * same shelf as the finals badge to keep the visual language
        * consistent. We skip the finals case because the badge above
        * already inlines the points alongside the PICK/FINAL scores. */}
      {!isFinal && pointsEarned != null && pointsEarned > 0 && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10 whitespace-nowrap rounded-md bg-emerald-500/15 px-2 py-0.5 ring-1 ring-emerald-500/50 text-[11px] font-bold tabular-nums text-emerald-300 shadow-sm">
          +{pointsEarned}
        </div>
      )}
    <div
      className={cn(
        'rounded-md overflow-hidden bg-card shadow-sm',
        isFinal ? 'border-2' : 'border',
        canPick &&
          'cursor-pointer hover:border-primary hover:shadow-md transition-all',
        isFinal && 'border-amber-400',
        isCompleted && 'border-emerald-500/40',
      )}
      onClick={() => {
        if (canPick) onPickMatch?.(match, ctx);
      }}
    >
      <CompetitorSlot
        pickNumber={firstPickNumber}
        competitor={topComp}
        isPredicted={topPredicted}
        isWinner={!!topId && match.winnerId === topId && isCompleted}
        isLoser={
          isCompleted && !!match.winnerId && !!topId && match.winnerId !== topId
        }
        isUserPick={!!topComp && !!userPickId && userPickId === topId}
        matchHasUserPick={!!userPickId}
        pickCorrect={
          isCompleted && !!userPickId
            ? userPickId === match.winnerId
            : undefined
        }
        pickedHere={!!userPickId && userPickId === topId}
        isFinal={isFinal}
        flowDirection={flowDirection}
      />
      <div className="border-t border-border" />
      <CompetitorSlot
        pickNumber={
          firstPickNumber === undefined ? undefined : firstPickNumber + 1
        }
        competitor={bottomComp}
        isPredicted={bottomPredicted}
        isWinner={!!bottomId && match.winnerId === bottomId && isCompleted}
        isLoser={
          isCompleted &&
          !!match.winnerId &&
          !!bottomId &&
          match.winnerId !== bottomId
        }
        isUserPick={!!bottomComp && !!userPickId && userPickId === bottomId}
        matchHasUserPick={!!userPickId}
        pickCorrect={
          isCompleted && !!userPickId
            ? userPickId === match.winnerId
            : undefined
        }
        pickedHere={!!userPickId && userPickId === bottomId}
        isFinal={isFinal}
        flowDirection={flowDirection}
      />
    </div>
    {/* Orphaned-pick footer: mirrors the top-of-card badge shelf so the
      * card has a clear "your pick didn't survive" indicator. X icon +
      * destructive tint reads as "out" without the unreadable strikethrough
      * on short numeric names. */}
    {orphanedPickName && (
      <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 whitespace-nowrap rounded-md bg-destructive/10 px-2 py-0.5 ring-1 ring-destructive/40 text-[11px] font-bold tabular-nums text-destructive/90 shadow-sm">
        <X className="h-3 w-3 flex-shrink-0" />
        <span className="text-destructive/60 font-normal">PICK</span>
        <span>{orphanedPickName}</span>
      </div>
    )}
    </div>
  );
}

function CompetitorSlot({
  pickNumber,
  competitor,
  isPredicted,
  isWinner,
  isLoser,
  isUserPick,
  matchHasUserPick,
  pickCorrect,
  pickedHere,
  isFinal,
  flowDirection,
}: {
  pickNumber?: number;
  competitor: PickemCompetitor | null | undefined;
  isPredicted: boolean;
  isWinner: boolean;
  isLoser: boolean;
  isUserPick: boolean;
  /** True when *either* slot in this match has been picked by the user.
   * Used to dim the non-picked sibling so the pick reads clearly at a
   * glance — without this the picked-row treatment alone is too subtle
   * on dense brackets. */
  matchHasUserPick: boolean;
  pickCorrect: boolean | undefined;
  pickedHere: boolean;
  isFinal?: boolean;
  /** Which side of a centered bracket this slot lives on — controls
   * which way the gold "correct pick" particles drift. */
  flowDirection?: 'ltr' | 'rtl';
}) {
  const slotHeight = isFinal ? 'h-16' : 'h-12';
  const avatarSize = isFinal ? 'h-11 w-11' : 'h-9 w-9';
  const nameSize = isFinal ? 'text-base' : 'text-sm';
  const pickBadge = pickNumber === undefined ? null : (
    <span
      role="img"
      title={`Pick ${pickNumber} · Match ${Math.ceil(pickNumber / 2)}`}
      aria-label={`Pick ${pickNumber}, Match ${Math.ceil(pickNumber / 2)}`}
      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/15 text-[11px] font-bold tabular-nums text-primary ring-1 ring-inset ring-primary/40"
    >
      {pickNumber}
    </span>
  );

  if (!competitor) {
    return (
      <div className={cn('flex items-center gap-2 px-2 py-1.5 border-l-[3px] border-l-transparent text-muted-foreground/40', slotHeight)}>
        {pickBadge}
        <div className={cn('rounded bg-muted/50', avatarSize)} />
        <span className={cn('italic', nameSize)}>TBD</span>
      </div>
    );
  }

  const isSiblingOfPick =
    matchHasUserPick && !isUserPick && !isWinner && !isLoser;

  return (
    <div
      className={cn(
        // Reserve a 3px left stripe on every row so the picked row's accent
        // doesn't reflow content vs the sibling row.
        'flex items-center gap-2 px-2 py-1.5 border-l-[3px] border-l-transparent transition-colors relative',
        slotHeight,
        isPredicted && !isWinner && !isLoser && 'opacity-70',
        // Winner that the user did NOT pick — bold name + green dot only.
        // No bg tint, so the green-tint treatment is reserved exclusively
        // for picks the user got right and doesn't make every winner row
        // look "selected" on a bracket where the user hasn't picked.
        isWinner && !isUserPick && 'font-semibold',
        // Loser that the user did NOT pick — fade it out.
        isLoser && !isUserPick && 'opacity-40',
        // The user's pick (no result yet): primary tint + accent stripe +
        // inset ring outlines the entire row to make the pick unmistakable.
        isUserPick && !isWinner && !isLoser &&
          'bg-primary/25 border-l-primary ring-1 ring-inset ring-primary/40',
        // The user's pick + it WON — strong emerald, success-outlined.
        // `isolate` makes the gold-particle layer (below) stack behind the
        // row's text via negative z-index without affecting siblings.
        isUserPick && isWinner &&
          'bg-emerald-500/25 border-l-emerald-500 ring-1 ring-inset ring-emerald-500/50 font-semibold isolate',
        // The user's pick + it LOST — strong destructive, error-outlined.
        isUserPick && isLoser &&
          'bg-destructive/20 border-l-destructive ring-1 ring-inset ring-destructive/50',
        // Sibling row in a match where the user has picked — fade so the
        // pick pops by contrast.
        isSiblingOfPick && 'opacity-50',
      )}
    >
      {pickBadge}
      {isUserPick && isWinner && (
        <CorrectPickParticles flowDirection={flowDirection} />
      )}
      {competitor.imageUrl ? (
        <img
          src={competitor.imageUrl}
          alt={competitor.name}
          className={cn('rounded object-cover flex-shrink-0', avatarSize)}
        />
      ) : (
        <div className={cn('rounded bg-muted flex items-center justify-center text-[10px] font-bold flex-shrink-0', avatarSize)}>
          {competitor.name.charAt(0)}
        </div>
      )}
      <span
        className={cn(
          'truncate flex-1',
          nameSize,
          isUserPick && !isWinner && !isLoser && 'font-semibold',
        )}
      >
        {competitor.name}
      </span>
      {pickedHere && pickCorrect === true && (
        <Check className="h-5 w-5 text-emerald-400 flex-shrink-0" />
      )}
      {pickedHere && pickCorrect === false && (
        <X className="h-5 w-5 text-destructive flex-shrink-0" />
      )}
      {pickedHere && pickCorrect === undefined && (
        <span
          className="rounded-sm bg-primary px-1.5 py-0.5 text-[9px] uppercase tracking-wide font-bold text-primary-foreground flex-shrink-0"
          title="Your pick"
        >
          Pick
        </span>
      )}
      {isWinner && !pickedHere && (
        <div className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
      )}
    </div>
  );
}

/**
 * Gold sparkle particles drifting through a correct pick row in the
 * direction the bracket flows. In the centered layout, matches on the
 * right side flow right → left (toward the central final), so their
 * particles run in reverse. Default is left → right.
 *
 * Sits behind the row's text (`-z-10` + parent `isolate`) but above the
 * row's bg fill.
 */
function CorrectPickParticles({
  flowDirection = 'ltr',
}: {
  flowDirection?: 'ltr' | 'rtl';
}) {
  const PARTICLES = [
    { top: '22%', delay: '0s', duration: '2.6s', size: 3 },
    { top: '38%', delay: '0.8s', duration: '3.2s', size: 2 },
    { top: '54%', delay: '1.5s', duration: '2.4s', size: 3 },
    { top: '68%', delay: '0.4s', duration: '3.6s', size: 2 },
    { top: '80%', delay: '2.1s', duration: '2.8s', size: 3 },
  ];
  const animationDirection = flowDirection === 'rtl' ? 'reverse' : 'normal';
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
    >
      {PARTICLES.map((p, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-amber-300"
          style={{
            top: p.top,
            width: `${p.size}px`,
            height: `${p.size}px`,
            boxShadow: '0 0 6px rgba(251, 191, 36, 0.85)',
            animationName: 'pickem-correct-particle',
            animationDuration: p.duration,
            animationTimingFunction: 'linear',
            animationDelay: p.delay,
            animationIterationCount: 'infinite',
            animationDirection,
          }}
        />
      ))}
    </div>
  );
}
